import { FC, useEffect, useMemo, useState } from 'react';
import { CustomModal, InfiniteLoadingSpinner } from 'components';
import { CheckboxTickIcon, CircleCloseIcon } from 'assets/icons';
import { Tag } from 'containers/tags/tags.model';
import CustomCheckbox from 'components/custom-checkbox/CustomCheckbox';
import CustomButton, { ButtonType } from 'components/custom-button/CustomButton';
import SearchBar from 'components/search/SearchBar';
import TagPill from 'containers/tags/create-tag/Tag';

interface JobTagListModalProps {
  open: boolean;
  onClose: () => void;
  selectedTags: Tag[];
  setSelectedTags: (tags: Tag[]) => void;
  allTags: Tag[];
  isLoading: boolean;
  jobNumber: string;
  canCreateTag?: boolean;
  onCreateTag?: () => void;
  isSaving?: boolean;
}

const JobTagListModal: FC<JobTagListModalProps> = ({
  open,
  onClose,
  selectedTags,
  setSelectedTags,
  allTags,
  isLoading,
  jobNumber,
  canCreateTag = false,
  onCreateTag,
  isSaving
}) => {
  const [search, setSearch] = useState('');
  const [localSelected, setLocalSelected] = useState<Tag[]>(selectedTags ?? []);

  useEffect(() => {
    setLocalSelected(selectedTags ?? []);
    if (!open) {
      setSearch('');
    }
  }, [selectedTags, open]);

  const filteredTags = useMemo(
    () => allTags.filter(tag => tag.name.toLowerCase().includes(search.toLowerCase())),
    [allTags, search]
  );

  const handleToggle = (tag: Tag) => {
    if (localSelected.some(t => t.id === tag.id)) {
      setLocalSelected(localSelected.filter(t => t.id !== tag.id));
    } else {
      setLocalSelected([...localSelected, tag]);
    }
  };

  const handleSave = () => {
    setSelectedTags(localSelected);
  };

  let content;
  if (isLoading) {
    content = <InfiniteLoadingSpinner />;
  } else if (filteredTags.length === 0) {
    content = <div className="p-4 text-center text-sbase text-accentText">No results found!</div>;
  } else {
    content = filteredTags.map(tag => (
      <div
        key={tag.id}
        className="flex cursor-pointer items-center border-b border-borderGray px-4 py-3 
        last:border-b-0"
        onClick={() => {
          handleToggle(tag);
        }}>
        <div className="flex flex-1 items-center gap-2">
          <TagPill tag={tag} className="px-2.5 py-1" textStyle="text-sbase font-semibold" />
        </div>
        <CustomCheckbox
          id={`tag-checkbox-${tag.id}`}
          isChecked={localSelected.some(t => t.id === tag.id)}
          onClick={() => handleToggle(tag)}
          className="ml-2">
          {localSelected.some(t => t.id === tag.id) && <CheckboxTickIcon className="text-primaryBtn " />}
        </CustomCheckbox>
      </div>
    ));
  }

  return (
    <CustomModal open={open} closeModal={onClose} width="md:w-[580px]" height="h-[644px]">
      <div className="w-full rounded-md bg-white p-6">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex flex-col gap-1">
            <div className="text-lg font-bold text-headingGray">Tags</div>
            <div className="text-sbase text-accentText">{jobNumber}</div>
          </div>
          <CircleCloseIcon className="h-6 w-6 cursor-pointer" onClick={onClose} />
        </div>
        <div className="rounded border border-borderGray p-4">
          <SearchBar
            id="tag-search"
            value={search}
            handleChange={setSearch}
            placeholder="Search tags"
            wrapperStyle="mb-3 bg-highlightGray rounded-[4px]"
            showSearchIcon={true}
            handleClear={() => setSearch('')}
            showCrossIcon={true}
            inputStyle="bg-transparent"
            autoFocus={true}
          />
          {canCreateTag && (
            <div
              className="mx-1 mt-2 cursor-pointer rounded-sm border-b border-borderGray p-2 text-sbase font-semibold
                 text-primaryBtn hover:shadow-sm"
              onClick={onCreateTag}>
              + Create tag
            </div>
          )}
          <div className="max-h-[340px] min-h-[340px] overflow-y-auto bg-white">{content}</div>
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <CustomButton id="tag-cancel-btn" type={ButtonType.SECONDARY} onClick={onClose} className="h-10 w-[100px]">
            Cancel
          </CustomButton>
          <CustomButton
            id="tag-save-btn"
            isLoading={isSaving}
            type={ButtonType.PRIMARY}
            onClick={handleSave}
            className="h-10 w-[100px]">
            Save
          </CustomButton>
        </div>
      </div>
    </CustomModal>
  );
};

export default JobTagListModal;
